import { PREP_TOUR_END, PrepTourDefinition, PrepTourStep } from './tour.types';

export type PrepTourValidation =
  | { ok: true; value: PrepTourDefinition }
  | { ok: false; errors: string[] };

const KINDS = new Set(['tour', 'recipe', 'fixit', 'missions']);
const ADVANCE = new Set(['next', 'click', 'event', 'predicate']);
const MISSING = new Set(['skip', 'explain', 'abort']);
const PLACEMENT = new Set(['top', 'bottom', 'left', 'right', 'auto']);

const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/**
 * Validate a guide definition with readable errors. Pure; no schema library.
 * `knownAnchors`, when given, turns unknown targets into errors — otherwise
 * they only fail at run time (`whenMissing`).
 */
export function validateTourDefinition(input: unknown, knownAnchors?: Iterable<string>): PrepTourValidation {
  const errors: string[] = [];
  const known = knownAnchors ? new Set(knownAnchors) : null;
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, errors: ['definition must be an object'] };
  }
  const d = input as Record<string, unknown>;
  if (!isStr(d['id'])) errors.push('id: required string');
  if (!KINDS.has(d['kind'] as string)) errors.push(`kind: one of ${Array.from(KINDS).join('|')}`);
  if (!isStr(d['title'])) errors.push('title: required string');
  if (d['version'] !== undefined && !(Number.isInteger(d['version']) && (d['version'] as number) >= 0)) {
    errors.push('version: non-negative integer');
  }
  const steps = d['steps'];
  if (!Array.isArray(steps) || steps.length === 0) {
    errors.push('steps: non-empty array');
  } else {
    const ids = new Set<string>();
    steps.forEach((raw, i) => validateStep(raw, i, ids, known, errors));
    // Static branch targets must exist (functions are resolved at run time).
    steps.forEach((raw, i) => {
      const s = raw as Record<string, unknown>;
      (['next', 'skipTo'] as const).forEach((key) => {
        const v = s[key];
        if (typeof v === 'string' && !ids.has(v) && v !== PREP_TOUR_END) errors.push(`steps[${i}].${key}: unknown step "${v}"`);
      });
    });
  }
  if (d['progress'] !== undefined && !['dots', 'counter', 'none'].includes(d['progress'] as string)) errors.push('progress: dots|counter|none');
  (['welcome', 'completion'] as const).forEach((key) => {
    const dlg = d[key];
    if (dlg === undefined) return;
    if (key === 'completion' && typeof dlg === 'function') return;
    if (!dlg || typeof dlg !== 'object') {
      errors.push(`${key}: object`);
      return;
    }
    const o = dlg as Record<string, unknown>;
    if (!isStr(o['title'])) errors.push(`${key}.title: required string`);
    if (!isStr(o['body'])) errors.push(`${key}.body: required string`);
  });
  return errors.length ? { ok: false, errors } : { ok: true, value: input as PrepTourDefinition };
}

function validateStep(raw: unknown, i: number, ids: Set<string>, known: Set<string> | null, errors: string[]): void {
  const at = `steps[${i}]`;
  if (!raw || typeof raw !== 'object') { errors.push(`${at}: object`); return; }
  const s = raw as Partial<PrepTourStep> & Record<string, unknown>;
  if (!isStr(s.id)) errors.push(`${at}.id: required string`);
  else if (ids.has(s.id)) errors.push(`${at}.id: duplicate "${s.id}"`);
  else ids.add(s.id);
  if (!isStr(s.title) && typeof s.title !== 'function') errors.push(`${at}.title: required string`);
  if (typeof s.body !== 'string' && typeof s.body !== 'function') errors.push(`${at}.body: string`);
  if (s.next !== undefined && typeof s.next !== 'string' && typeof s.next !== 'function') errors.push(`${at}.next: step id or function`);
  if (s.skipTo !== undefined && !isStr(s.skipTo) && typeof s.skipTo !== 'function') errors.push(`${at}.skipTo: step id or function`);
  const actions = s.action === undefined ? [] : Array.isArray(s.action) ? s.action : [s.action];
  for (const rawAct of actions) {
    const act = rawAct as unknown as Record<string, unknown> | null;
    if (!act || typeof act !== 'object' || typeof act['run'] !== 'function' || !(isStr(act['label']) || typeof act['label'] === 'function')) {
      errors.push(`${at}.action: { label, run() }`);
    }
  }
  const pager = s.pager as unknown as Record<string, unknown> | undefined;
  if (pager !== undefined && (!pager || typeof pager !== 'object' || ['index', 'count', 'prev', 'next'].some((k) => typeof pager[k] !== 'function'))) {
    errors.push(`${at}.pager: { index(), count(), prev(), next() }`);
  }
  if (s.target !== undefined) {
    if (!isStr(s.target)) errors.push(`${at}.target: string`);
    else if (known && !known.has(s.target)) errors.push(`${at}.target: unknown anchor "${s.target}"`);
  }
  if (s.placement !== undefined && !PLACEMENT.has(s.placement)) errors.push(`${at}.placement: top|bottom|left|right|auto`);
  if (s.whenMissing !== undefined && !MISSING.has(s.whenMissing)) errors.push(`${at}.whenMissing: skip|explain|abort`);
  const adv = s.advance as Record<string, unknown> | undefined;
  if (adv !== undefined) {
    if (!adv || typeof adv !== 'object' || !ADVANCE.has(adv['kind'] as string)) {
      errors.push(`${at}.advance.kind: next|click|event|predicate`);
    } else if (adv['kind'] === 'event' && !isStr(adv['name'])) {
      errors.push(`${at}.advance.name: required for kind "event"`);
    } else if (adv['kind'] === 'predicate' && typeof adv['satisfied'] !== 'function') {
      errors.push(`${at}.advance.satisfied: function required for kind "predicate"`);
    } else if (adv['kind'] === 'click' && !s.target && !isStr(adv['anchor'])) {
      errors.push(`${at}.advance: "click" needs a target or an anchor`);
    }
  }
}
